<template>
  <b-table
    :items="items"
    :fields="headers"
    :busy="loading"
    :sort-by.sync="localSortBy"
    :sort-desc.sync="localSortDesc"
    :tbody-tr-class="rowClass"
    thead-class="list-header"
    borderless
    hover
    responsive
    show-empty
    no-local-sorting
    @row-clicked="$emit('row-clicked', $event)"
    @sort-changed="emitSortChanged"
  >
    <template #cell(company)="slotData">
      <span class="company-name">
        {{
          (slotData.item.company && slotData.item.company.fantasyName) || "—"
        }}
      </span>
    </template>

    <template #cell(slug)="slotData">
      <strong class="slug-text">{{ slotData.item.slug }}</strong>
    </template>

    <template #cell(createdAt)="slotData">
      <span class="date-text">{{ slotData.item.createdAt | datetime }}</span>
    </template>

    <template #cell(updatedAt)="slotData">
      <span class="date-text">{{ slotData.item.updatedAt | datetime }}</span>
    </template>

    <template #cell(status)="slotData">
      <b-badge v-if="slotData.item.deletedAt" variant="dark">Excluído</b-badge>
      <b-badge v-else-if="slotData.item.active === false" variant="secondary"
        >Oculto</b-badge
      >
      <b-badge v-else variant="success">Ativo</b-badge>
    </template>

    <template #cell(actions)="slotData">
      <div class="actions-wrapper">
        <b-button
          size="sm"
          variant="link"
          class="action-btn edit-btn"
          title="Editar"
          @click.stop="$emit('edit', slotData.item)"
        >
          <i class="fa fa-edit" aria-hidden="true" />
        </b-button>
        <b-button
          v-if="!slotData.item.deletedAt"
          size="sm"
          variant="link"
          class="action-btn visibility-btn"
          :title="slotData.item.active === false ? 'Exibir' : 'Ocultar'"
          @click.stop="$emit('toggle-active', slotData.item)"
        >
          <i
            class="fa"
            :class="slotData.item.active === false ? 'fa-eye-slash' : 'fa-eye'"
            aria-hidden="true"
          />
        </b-button>
        <b-button
          v-if="!slotData.item.deletedAt"
          size="sm"
          variant="link"
          class="action-btn delete-btn"
          title="Excluir"
          @click.stop="$emit('delete', slotData.item)"
        >
          <i class="fa fa-trash" aria-hidden="true" />
        </b-button>
        <b-button
          v-else
          size="sm"
          variant="link"
          class="action-btn restore-btn"
          title="Restaurar"
          @click.stop="$emit('restore', slotData.item)"
        >
          <i class="fa fa-undo" aria-hidden="true" />
        </b-button>
      </div>
    </template>

    <template #table-busy>
      <div class="text-center py-5">
        <b-spinner></b-spinner>
        <span class="ml-2">Carregando...</span>
      </div>
    </template>
    <template #empty>
      <div class="text-muted text-center py-5">
        <i class="fas fa-check-circle fa-2x"></i>
        <p class="mt-3">Nenhum portal encontrado</p>
      </div>
    </template>
  </b-table>
</template>

<script>
export default {
  name: "PortalsTable",
  props: {
    items: { type: Array, required: true },
    headers: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    sortBy: { type: String, default: null },
    sortDesc: { type: Boolean, default: false },
  },
  data() {
    return {
      localSortBy: this.sortBy,
      localSortDesc: this.sortDesc,
    };
  },
  watch: {
    sortBy(newVal) {
      this.localSortBy = newVal;
    },
    sortDesc(newVal) {
      this.localSortDesc = newVal;
    },
  },
  methods: {
    emitSortChanged(sortOptions) {
      this.$emit("sort-changed", sortOptions);
    },
    rowClass(item) {
      const classes = ["portal-row"];
      if (item && item.deletedAt) classes.push("portal-row--deleted");
      else if (item && item.active === false)
        classes.push("portal-row--hidden");
      return classes.join(" ");
    },
  },
};
</script>

<style scoped>
.actions-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  padding: 0 !important;
  margin-right: 6px;
  border: none;
  transition: all 0.2s ease;
  text-decoration: none !important;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  filter: brightness(0.95);
}

.action-btn i {
  font-size: 1rem;
  margin: 0;
}

.edit-btn {
  background-color: #e8eaf6 !important;
  color: #3f51b5 !important;
}

.visibility-btn {
  background-color: #e0f7fa !important;
  color: #00838f !important;
}

.delete-btn {
  background-color: #ffebee !important;
  color: #f44336 !important;
}

.restore-btn {
  background-color: #e8f5e9 !important;
  color: #4caf50 !important;
}

::v-deep .list-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

::v-deep .list-header th.sortable:hover {
  color: #0056b3;
}

::v-deep .portal-row {
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

::v-deep .portal-row:hover {
  background-color: #e9ecef !important;
}

::v-deep tbody tr:last-of-type {
  border-bottom: none;
}

::v-deep .portal-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

::v-deep .slug-text {
  color: #007bff;
  font-weight: 500;
}

::v-deep .company-name,
::v-deep .date-text {
  color: #6c757d;
}

::v-deep .action-icon {
  color: #6c757d;
  transition: color 0.2s;
}

::v-deep .portal-row:hover .action-icon {
  color: #007bff;
}

::v-deep .portal-row--hidden td {
  opacity: 0.55;
}

::v-deep .portal-row--deleted td {
  opacity: 0.4;
  text-decoration: line-through;
}
</style>
